import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Check, Target, Globe, MapPin, Zap, BarChart3,
  Users, Megaphone, TrendingUp, Eye, Award, Layers, Monitor,
  ChevronRight, Star, Activity, Send, Loader2, Mail, User, Building2, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../services/api';

const targetCards = [
  { color: '#22c55e', icon: Target, key: 'event' },
  { color: '#3b82f6', icon: MapPin, key: 'region' },
  { color: '#8b5cf6', icon: Activity, key: 'sport' },
  { color: '#ef4444', icon: Globe, key: 'national' },
  { color: '#f59e0b', icon: Layers, key: 'premium' },
];

const advIcons = [Target, Users, BarChart3, Megaphone, Award, Monitor];

const SponsoringPage = () => {
  const { t } = useTranslation();
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', phone: '', budget: '', target_type: '', message: '' });
  const [contactSending, setContactSending] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      await api.post('/sponsors/leads', contactForm);
      toast.success(t('sponsorContact.success'));
      setContactForm({ name: '', email: '', company: '', phone: '', budget: '', target_type: '', message: '' });
    } catch {
      toast.error(t('sponsorContact.error'));
    } finally {
      setContactSending(false);
    }
  };

  const stats = [
    { value: t('sponsor.stat1Value'), label: t('sponsor.stat1Label') },
    { value: t('sponsor.stat2Value'), label: t('sponsor.stat2Label') },
    { value: t('sponsor.stat3Value'), label: t('sponsor.stat3Label') },
    { value: t('sponsor.stat4Value'), label: t('sponsor.stat4Label') },
  ];

  const targets = targetCards.map(tc => ({
    ...tc,
    title: t(`sponsor.${tc.key}TargetTitle`),
    desc: t(`sponsor.${tc.key}TargetDesc`),
    price: t(`sponsor.${tc.key}TargetPrice`),
  }));

  const advantages = advIcons.map((icon, idx) => ({
    icon,
    title: t(`sponsor.adv${idx + 1}Title`),
    desc: t(`sponsor.adv${idx + 1}Desc`),
  }));

  const brands = [
    { name: 'NIKE', color: '#111' },
    { name: 'DECATHLON', color: '#0066CC' },
    { name: 'SALOMON', color: '#E4002B' },
    { name: 'GARMIN', color: '#007CC3' },
    { name: 'HOKA', color: '#1A1A1A' },
    { name: 'SPECIALIZED', color: '#CF0A2C' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative bg-asphalt text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-brand/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="max-w-3xl">
            <motion.div
              className="inline-flex items-center gap-2 bg-brand/10 border border-brand/20 px-4 py-2 mb-8"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            >
              <Megaphone className="w-4 h-4 text-brand" />
              <span className="text-brand font-heading font-bold uppercase tracking-widest text-xs">{t('sponsor.badge')}</span>
            </motion.div>

            <motion.h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-tight"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            >
              {t('sponsor.heroTitle')}
            </motion.h1>

            <motion.p
              className="text-lg text-slate-300 mt-6 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            >
              {t('sponsor.heroDesc')}
            </motion.p>

            <motion.div className="flex flex-wrap gap-4 mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              <Link to="/register?role=sponsor">
                <Button className="btn-primary h-14 px-8 text-base gap-2" data-testid="sponsor-cta-hero">
                  {t('sponsor.heroCta')} <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div className="flex items-center gap-8 mt-10 text-sm text-slate-400" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('sponsor.heroCheck1')}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('sponsor.heroCheck2')}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('sponsor.heroCheck3')}</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Positioning banner */}
      <section className="bg-brand text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-heading text-sm md:text-base uppercase tracking-wider">
            "{t('sponsor.positioning')}"
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-slate-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <div className="font-heading text-4xl font-bold text-brand">{s.value}</div>
                <div className="text-sm text-slate-400 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted brands */}
      <section className="py-12 bg-slate-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-12 flex-wrap opacity-40">
            {brands.map((b, i) => (
              <span key={i} className="font-heading font-bold text-2xl tracking-widest" style={{ color: b.color }}>
                {b.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Targeting options */}
      <section className="py-24" data-testid="sponsor-targeting">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('sponsor.targetLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">{t('sponsor.targetTitle')}</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">{t('sponsor.targetDesc')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {targets.map((target, idx) => (
              <motion.div
                key={idx}
                className="relative p-8 border-2 border-slate-200 hover:border-current transition-colors group overflow-hidden"
                style={{ '--tw-border-opacity': 1 }}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
              >
                <div
                  className="absolute top-0 left-0 w-1 h-full transition-all group-hover:w-2"
                  style={{ background: target.color }}
                />
                <target.icon className="w-10 h-10 mb-4" style={{ color: target.color }} />
                <h3 className="font-heading font-bold text-xl uppercase tracking-wider mb-3">{target.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-6">{target.desc}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-heading font-bold" style={{ color: target.color }}>{target.price}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-24 bg-slate-50" data-testid="sponsor-advantages">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('sponsor.advantagesLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">{t('sponsor.advantagesTitle')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {advantages.map((a, idx) => (
              <motion.div
                key={idx}
                className="p-8 bg-white border border-slate-200 hover:border-brand group transition-all"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.06 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
              >
                <a.icon className="w-10 h-10 text-brand mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-2">{a.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Example campaigns */}
      <section className="py-24 bg-asphalt text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">Exemples</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">Cas concrets</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { sport: 'Trail Corse', brand: 'Salomon', price: '200€', color: '#22c55e', desc: 'Visibilite sur tous les trails en Corse. Micro-ciblage local avec forte intention d\'achat.' },
              { sport: 'Triathlon PACA', brand: 'Specialized', price: '1 000€/mois', color: '#3b82f6', desc: 'Couverture regionale sur tous les triathlons. Audience velo + natation + running.' },
              { sport: 'National Fitness', brand: 'Nike', price: '5 000€/mois', color: '#ef4444', desc: 'Visibilite sur toute la plateforme. Homepage + newsletter + toutes les pages evenements.' },
            ].map((ex, idx) => (
              <motion.div
                key={idx}
                className="border border-slate-700 p-8"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full" style={{ background: ex.color }} />
                  <span className="font-heading font-bold uppercase tracking-wider text-sm">{ex.sport}</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{ex.desc}</p>
                <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                  <span className="text-slate-500 text-xs">{ex.brand}</span>
                  <span className="font-heading font-bold text-lg" style={{ color: ex.color }}>{ex.price}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-white" id="contact" data-testid="sponsor-contact-form">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - Info */}
            <div>
              <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('sponsorContact.title')}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">{t('sponsorContact.subtitle')}</h2>
              <div className="mt-8 space-y-4">
                {[
                  { icon: Target, text: 'Ciblage ultra-precis par sport, region et evenement' },
                  { icon: BarChart3, text: 'Dashboard analytics temps reel avec ROI mesurable' },
                  { icon: Users, text: 'Audience 100% sportifs actifs, forte intention d\'achat' },
                  { icon: Award, text: 'Accompagnement personnalise par un expert media sport' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="p-2 bg-brand/10 flex-shrink-0">
                      <item.icon className="w-4 h-4 text-brand" />
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Form */}
            <motion.div
              className="bg-slate-50 border border-slate-200 p-8"
              initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            >
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">{t('sponsorContact.name')} *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="text" required value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))}
                        className="w-full h-11 pl-10 pr-4 border border-slate-200 bg-white text-sm outline-none focus:border-brand transition-colors"
                        data-testid="contact-name" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">{t('sponsorContact.email')} *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="email" required value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))}
                        className="w-full h-11 pl-10 pr-4 border border-slate-200 bg-white text-sm outline-none focus:border-brand transition-colors"
                        data-testid="contact-email" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">{t('sponsorContact.company')} *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="text" required value={contactForm.company} onChange={e => setContactForm(p => ({ ...p, company: e.target.value }))}
                        className="w-full h-11 pl-10 pr-4 border border-slate-200 bg-white text-sm outline-none focus:border-brand transition-colors"
                        data-testid="contact-company" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">{t('sponsorContact.phone')}</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="tel" value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))}
                        className="w-full h-11 pl-10 pr-4 border border-slate-200 bg-white text-sm outline-none focus:border-brand transition-colors"
                        data-testid="contact-phone" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">{t('sponsorContact.budget')}</label>
                    <select value={contactForm.budget} onChange={e => setContactForm(p => ({ ...p, budget: e.target.value }))}
                      className="w-full h-11 px-3 border border-slate-200 bg-white text-sm outline-none focus:border-brand transition-colors"
                      data-testid="contact-budget">
                      <option value="">{t('sponsorContact.budgetPlaceholder')}</option>
                      <option value="small">{t('sponsorContact.budgetOptions.small')}</option>
                      <option value="medium">{t('sponsorContact.budgetOptions.medium')}</option>
                      <option value="large">{t('sponsorContact.budgetOptions.large')}</option>
                      <option value="enterprise">{t('sponsorContact.budgetOptions.enterprise')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">{t('sponsorContact.targetType')}</label>
                    <select value={contactForm.target_type} onChange={e => setContactForm(p => ({ ...p, target_type: e.target.value }))}
                      className="w-full h-11 px-3 border border-slate-200 bg-white text-sm outline-none focus:border-brand transition-colors"
                      data-testid="contact-target">
                      <option value="">{t('sponsorContact.budgetPlaceholder')}</option>
                      <option value="event">{t('sponsor.eventTargetTitle')}</option>
                      <option value="region">{t('sponsor.regionTargetTitle')}</option>
                      <option value="sport">{t('sponsor.sportTargetTitle')}</option>
                      <option value="national">{t('sponsor.nationalTargetTitle')}</option>
                      <option value="premium">{t('sponsor.premiumTargetTitle')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-heading uppercase tracking-widest text-slate-500 mb-1.5 block">{t('sponsorContact.message')}</label>
                  <textarea rows={3} value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                    placeholder={t('sponsorContact.messagePlaceholder')}
                    className="w-full p-3 border border-slate-200 bg-white text-sm outline-none focus:border-brand transition-colors resize-none"
                    data-testid="contact-message" />
                </div>

                <Button type="submit" disabled={contactSending} className="btn-primary w-full h-12 text-sm gap-2" data-testid="contact-submit">
                  {contactSending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t('sponsorContact.sending')}</>
                  ) : (
                    <><Send className="w-4 h-4" /> {t('sponsorContact.submit')}</>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 className="font-heading text-3xl md:text-5xl font-bold uppercase" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            {t('sponsor.ctaTitle')}
          </motion.h2>
          <motion.p className="text-lg text-white/80 mt-4" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.1 }} viewport={{ once: true }}>
            {t('sponsor.ctaDesc')}
          </motion.p>
          <motion.div className="mt-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} viewport={{ once: true }}>
            <Link to="/register?role=sponsor">
              <Button className="bg-white text-brand hover:bg-slate-100 h-14 px-8 text-base font-heading font-bold uppercase tracking-wider gap-2" data-testid="sponsor-cta-final">
                {t('sponsor.ctaCta')} <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <p className="text-sm text-white/60 mt-4">{t('sponsor.ctaFooter')}</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SponsoringPage;
